import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { VoiceChat } from '../components/VoiceChat'
import { GameLobby } from '../components/GameLobby'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import { BigTwoAI } from '../utils/ai/bigtwo'
import { GameRoomSidebar, GameRoomHeader } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'
import '../styles/GamePage.css'
import '../styles/BigTwo.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'

// Card types and rankings
type Suit = '♠' | '♥' | '♣' | '♦'
type Rank = '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A' | '2'

interface Card {
  suit: Suit
  rank: Rank
  id: string
}

type GamePhase = 'select' | 'setup' | 'lobby' | 'playing' | 'finished'
type GameMode = 'select' | 'local' | 'network' | 'ai'
type AIDifficulty = 'easy' | 'medium' | 'hard'

const RANK_ORDER: Rank[] = ['3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2']
const SUITS: Suit[] = ['♦', '♣', '♥', '♠'] // Diamond < Club < Heart < Spade

export default function BigTwo() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('select')
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium')
  const [lobbyKey, setLobbyKey] = useState(0)

  // AI instance
  const aiInstanceRef = useRef<BigTwoAI | null>(null)

  // Player info
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)

  // Network multiplayer
  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined, // Will be set after creation/join
    playerNumber,
    playerName,
  })

  const {
    session,
    createSession,
    joinSession,
    voice,
    
    sendMove,
    onMessage,
  } = enhancedSession

  // Game state
  const [player1Hand, setPlayer1Hand] = useState<Card[]>([])
  const [player2Hand, setPlayer2Hand] = useState<Card[]>([])
  const [selectedCards, setSelectedCards] = useState<string[]>([])
  const [lastPlay, setLastPlay] = useState<Card[]>([])
  const [currentTurn, setCurrentTurn] = useState<1 | 2>(1)
  const [winner, setWinner] = useState<1 | 2 | null>(null)
  const [passCount, setPassCount] = useState(0)
  const [playError, setPlayError] = useState<string | null>(null)

  // Sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  const addHistory = (content: string, type: HistoryItem['type'] = 'move') => {
    setHistoryItems(prev => [...prev, { type, content, timestamp: Date.now() }])
  }

  // Create deck
  const createDeck = (): Card[] => {
    const cards: Card[] = []
    for (const suit of SUITS) {
      for (const rank of RANK_ORDER) {
        cards.push({ suit, rank, id: `${suit}${rank}` })
      }
    }
    return cards.sort(() => Math.random() - 0.5)
  }

  // Deal cards
  const dealCards = () => {
    const newDeck = createDeck()
    const p1 = newDeck.slice(0, 13).sort((a, b) => compareCards(a, b))
    const p2 = newDeck.slice(13, 26).sort((a, b) => compareCards(a, b))

    setPlayer1Hand(p1)
    setPlayer2Hand(p2)
    setLastPlay([])
    setPassCount(0)

    // Player with 3 of Diamonds starts
    const hasThreeDiamonds = (hand: Card[]) => hand.some(c => c.rank === '3' && c.suit === '♦')
    setCurrentTurn(hasThreeDiamonds(p1) ? 1 : 2)
  }

  // Compare cards (for sorting)
  const compareCards = (a: Card, b: Card): number => {
    const rankDiff = RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank)
    if (rankDiff !== 0) return rankDiff
    return SUITS.indexOf(a.suit) - SUITS.indexOf(b.suit)
  }

  // Sort current player's hand
  const sortCurrentHand = () => {
    if (gameMode === 'ai') {
      setPlayer1Hand(h => [...h].sort(compareCards))
    } else if (gameMode === 'local') {
      if (currentTurn === 1) setPlayer1Hand(h => [...h].sort(compareCards))
      else setPlayer2Hand(h => [...h].sort(compareCards))
    } else {
      if (playerNumber === 1) setPlayer1Hand(h => [...h].sort(compareCards))
      else setPlayer2Hand(h => [...h].sort(compareCards))
    }
    setSelectedCards([])
  }

  // Check if cards form a valid play
  const isValidPlay = (cards: Card[]): { valid: boolean; type: string; rank?: number } => {
    if (cards.length === 0) return { valid: false, type: '' }

    // Single card
    if (cards.length === 1) return { valid: true, type: 'single' }

    // Pair
    if (cards.length === 2 && cards[0].rank === cards[1].rank) {
      return { valid: true, type: 'pair' }
    }

    // Triple
    if (cards.length === 3 && cards[0].rank === cards[1].rank && cards[1].rank === cards[2].rank) {
      return { valid: true, type: 'triple' }
    }

    // 5-card combos
    if (cards.length === 5) {
      const sorted = [...cards].sort((a, b) => RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank))

      // Check for straight (5 consecutive ranks)
      let isStraight = true
      for (let i = 0; i < 4; i++) {
        if (RANK_ORDER.indexOf(sorted[i + 1].rank) !== RANK_ORDER.indexOf(sorted[i].rank) + 1) {
          isStraight = false
          break
        }
      }

      // Check for flush (all same suit)
      const isFlush = cards.every(card => card.suit === cards[0].suit)

      // Straight Flush (rank 5)
      if (isStraight && isFlush) {
        return { valid: true, type: 'straight-flush', rank: 5 }
      }

      // Check for Four of a Kind (rank 4)
      const rankCounts: { [key: string]: number } = {}
      cards.forEach(card => {
        rankCounts[card.rank] = (rankCounts[card.rank] || 0) + 1
      })
      const counts = Object.values(rankCounts)
      if (counts.includes(4)) {
        return { valid: true, type: 'four-of-a-kind', rank: 4 }
      }

      // Check for Full House (rank 3)
      if (counts.includes(3) && counts.includes(2)) {
        return { valid: true, type: 'full-house', rank: 3 }
      }

      // Flush (rank 2)
      if (isFlush) {
        return { valid: true, type: 'flush', rank: 2 }
      }

      // Straight (rank 1)
      if (isStraight) {
        return { valid: true, type: 'straight', rank: 1 }
      }
    }

    return { valid: false, type: '' }
  }

  // Check if play beats last play
  const beatsLastPlay = (cards: Card[]): boolean => {
    // Free play if table is clear OR if opponent just passed
    if (lastPlay.length === 0 || passCount === 1) return true

    const newPlay = isValidPlay(cards)
    const oldPlay = isValidPlay(lastPlay)

    if (!newPlay.valid) return false
    if (cards.length !== lastPlay.length) return false

    // For 5-card combos, use combo ranking system
    if (cards.length === 5 && newPlay.rank !== undefined && oldPlay.rank !== undefined) {
      // Higher rank combo always wins (Straight < Flush < Full House < Four of a Kind < Straight Flush)
      if (newPlay.rank !== oldPlay.rank) {
        return newPlay.rank > oldPlay.rank
      }

      // Same rank combo - compare by card values
      if (newPlay.type === 'full-house' && oldPlay.type === 'full-house') {
        // Full House: compare triple rank
        const newRanks = cards.map(c => c.rank)
        const oldRanks = lastPlay.map(c => c.rank)
        const newTriple = newRanks.find(r => newRanks.filter(x => x === r).length === 3)!
        const oldTriple = oldRanks.find(r => oldRanks.filter(x => x === r).length === 3)!
        return RANK_ORDER.indexOf(newTriple) > RANK_ORDER.indexOf(oldTriple)
      }

      if (newPlay.type === 'four-of-a-kind' && oldPlay.type === 'four-of-a-kind') {
        // Four of a Kind: compare the four cards rank
        const newRanks = cards.map(c => c.rank)
        const oldRanks = lastPlay.map(c => c.rank)
        const newFour = newRanks.find(r => newRanks.filter(x => x === r).length === 4)!
        const oldFour = oldRanks.find(r => oldRanks.filter(x => x === r).length === 4)!
        return RANK_ORDER.indexOf(newFour) > RANK_ORDER.indexOf(oldFour)
      }

      // Straight, Flush, Straight Flush: compare highest card
      const newSorted = [...cards].sort((a, b) => compareCards(b, a))
      const oldSorted = [...lastPlay].sort((a, b) => compareCards(b, a))
      return compareCards(newSorted[0], oldSorted[0]) > 0
    }

    // For non-5-card combos, must match type exactly
    if (newPlay.type !== oldPlay.type) return false

    // Compare highest card
    const newHighest = [...cards].sort((a, b) => compareCards(b, a))[0]
    const oldHighest = [...lastPlay].sort((a, b) => compareCards(b, a))[0]

    return compareCards(newHighest, oldHighest) > 0
  }

  // Play selected cards
  const playCards = () => {
    setPlayError(null)
    const currentHand = currentTurn === 1 ? player1Hand : player2Hand
    const cards = selectedCards.map(id => currentHand.find(c => c.id === id)!).filter(Boolean)

    // Must include 3 of Diamonds on first move
    if (lastPlay.length === 0 && passCount === 0) {
      const has3D = cards.some(c => c.rank === '3' && c.suit === '♦')
      if (!has3D) {
        setPlayError('First play must include 3 of Diamonds!')
        return
      }
    }

    const playCheck = isValidPlay(cards)
    if (!playCheck.valid) {
      setPlayError('Invalid card combination!')
      return
    }

    if (!beatsLastPlay(cards)) {
      setPlayError("Doesn't beat the last play!")
      return
    }

    // Remove cards from hand
    const newHand = currentHand.filter(c => !selectedCards.includes(c.id))

    if (currentTurn === 1) {
      setPlayer1Hand(newHand)
    } else {
      setPlayer2Hand(newHand)
    }

    setLastPlay(cards)
    setSelectedCards([])
    setPassCount(0)
    sounds.click()

    const playDesc = cards.map(c => `${c.rank}${c.suit}`).join(' ')
    addHistory(`Player ${currentTurn} played: ${playDesc}`, 'move')

    // Check win
    if (newHand.length === 0) {
      setWinner(currentTurn)
      setPhase('finished')
      sounds.success()
      addHistory(`Player ${currentTurn} wins!`, 'event')

      // Send final state
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          player1Hand: currentTurn === 1 ? newHand : player1Hand,
          player2Hand: currentTurn === 2 ? newHand : player2Hand,
          lastPlay: cards,
          currentTurn,
          passCount: 0,
          winner: currentTurn,
        }})
      }
      return
    }

    const nextTurn = currentTurn === 1 ? 2 : 1
    setCurrentTurn(nextTurn)
    sounds.turnChange()

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        player1Hand: currentTurn === 1 ? newHand : player1Hand,
        player2Hand: currentTurn === 2 ? newHand : player2Hand,
        lastPlay: cards,
        currentTurn: nextTurn,
        passCount: 0,
      }})
    }
  }

  // Pass turn
  const pass = () => {
    setPlayError(null)
    if (lastPlay.length === 0) {
      setPlayError('Cannot pass — table is empty, you must play!')
      return
    }

    const newPassCount = passCount + 1
    setPassCount(newPassCount)

    // If both players pass, clear the table
    const newLastPlay = newPassCount >= 2 ? [] : lastPlay
    const finalPassCount = newPassCount >= 2 ? 0 : newPassCount

    if (newPassCount >= 2) {
      setLastPlay([])
      setPassCount(0)
    }

    const nextTurn = currentTurn === 1 ? 2 : 1
    setCurrentTurn(nextTurn)
    setSelectedCards([])
    sounds.click()
    addHistory(`Player ${currentTurn} passed`, 'move')

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        player1Hand,
        player2Hand,
        lastPlay: newLastPlay,
        currentTurn: nextTurn,
        passCount: finalPassCount,
      }})
    }
  }

  // AI turn logic
  useEffect(() => {
    if (gameMode !== 'ai') return
    if (phase !== 'playing') return
    if (currentTurn !== 2) return // AI is player 2
    if (!aiInstanceRef.current) return
    if (winner) return

    // Update AI's hand and last play
    aiInstanceRef.current.setHand(player2Hand)
    aiInstanceRef.current.setLastPlay(lastPlay)

    // AI makes decision after delay
    const delay = aiDifficulty === 'easy' ? 1500 : aiDifficulty === 'medium' ? 1200 : 800
    const timer = setTimeout(() => {
      if (!aiInstanceRef.current) return

      const decision = aiInstanceRef.current.play()

      if (decision === 'pass') {
        // AI passes
        const newPassCount = passCount + 1

        if (newPassCount >= 2) {
          setLastPlay([])
          setPassCount(0)
          aiInstanceRef.current.resetPassCount()
        } else {
          setPassCount(newPassCount)
        }

        setCurrentTurn(1)
        sounds.click()
        addHistory('AI passed', 'move')
      } else {
        // AI plays cards
        const cards = decision
        aiInstanceRef.current.removeCards(cards)

        const newHand = player2Hand.filter(c => !cards.some(ac => ac.id === c.id))
        setPlayer2Hand(newHand)
        setLastPlay(cards)
        setPassCount(0)
        aiInstanceRef.current.resetPassCount()
        sounds.click()
        const aiPlayDesc = cards.map((c: Card) => `${c.rank}${c.suit}`).join(' ')
        addHistory(`AI played: ${aiPlayDesc}`, 'move')

        // Check win
        if (newHand.length === 0) {
          setWinner(2)
          setPhase('finished')
          sounds.success()
          addHistory('AI wins!', 'event')
          return
        }

        setCurrentTurn(1)
        sounds.turnChange()
      }
    }, delay)

    return () => clearTimeout(timer)
  }, [gameMode, phase, currentTurn, player2Hand, lastPlay, passCount, aiDifficulty, winner])

  // Handler for sidebar message sending
  const handleSidebarMessage = async (message: string) => {
    if (gameMode === 'network') {
      await sendMove({ type: 'chat', message })
      // Add to local messages
      const newMessage: Message = {
        sender: 'you',
        content: message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    }
  }

  // Sync game state (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('game_state', (data) => {
      const state = data.state
      if (state.player1Hand) setPlayer1Hand(state.player1Hand)
      if (state.player2Hand) setPlayer2Hand(state.player2Hand)
      if (state.lastPlay) setLastPlay(state.lastPlay)
      if (state.currentTurn) setCurrentTurn(state.currentTurn)
      if (state.passCount !== undefined) setPassCount(state.passCount)
      if (state.winner !== undefined) {
        setWinner(state.winner)
        if (state.winner) setPhase('finished')
      }
    })

    const unsubscribeChat = onMessage('chat', (data) => {
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribe()
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  // Check if player joined
  useEffect(() => {
    if (gameMode === 'network' && phase !== 'finished' && session?.player2_name && phase !== 'playing') {
      setPhase('playing')
      sounds.playerJoined()
    }
  }, [session?.player2_name, phase, gameMode])

  // Toggle card selection
  const toggleCard = (cardId: string) => {
    if (selectedCards.includes(cardId)) {
      setSelectedCards(selectedCards.filter(id => id !== cardId))
    } else {
      setSelectedCards([...selectedCards, cardId])
    }
    sounds.click()
  }

  // Pre-game lobby (select / setup / waiting)
  if (phase !== 'playing' && phase !== 'finished') {
    return (
      <GameLobby
        key={lobbyKey}
        gameTitle="大老二 (Big Two)"
        gameIcon="🎴"
        gameDescription="Be the first to empty your hand — 2 to 4 players"
        gameBanner="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        supportedModes={['local', 'ai', 'network']}
        initialJoinCode={autoJoinCode || ''}
        networkLoading={enhancedSession.loading}
        networkError={enhancedSession.error}
        sessionCode={phase === 'lobby' ? session?.session_code : undefined}
        onBack={() => navigate('/')}
        onStartLocal={(name) => {
          setPlayerName(name)
          setGameMode('local')
          dealCards()
          setPhase('playing')
        }}
        onStartAI={(name, diff) => {
          setPlayerName(name)
          setGameMode('ai')
          setAiDifficulty(diff)
          aiInstanceRef.current = new BigTwoAI()
          dealCards()
          setPhase('playing')
        }}
        onCreateNetwork={async (name) => {
          setPlayerName(name)
          setGameMode('network')
          setPlayerNumber(1)
          await createSession('bigtwo', name)
          const newDeck = createDeck()
          const p1 = newDeck.slice(0, 13).sort((a, b) => compareCards(a, b))
          const p2 = newDeck.slice(13, 26).sort((a, b) => compareCards(a, b))
          const hasThreeDiamonds = (hand: Card[]) => hand.some(c => c.rank === '3' && c.suit === '♦')
          const firstTurn = hasThreeDiamonds(p1) ? 1 : 2
          setPlayer1Hand(p1)
          setPlayer2Hand(p2)
          setLastPlay([])
          setPassCount(0)
          setCurrentTurn(firstTurn as 1 | 2)
          sendMove({ type: 'game_state', state: { player1Hand: p1, player2Hand: p2, lastPlay: [], currentTurn: firstTurn, passCount: 0 } })
          setPhase('lobby')
        }}
        onJoinNetwork={async (name, code) => {
          setPlayerName(name)
          setGameMode('network')
          setPlayerNumber(2)
          await joinSession(code, name)
          setPhase('playing')
        }}
        onCancelWaiting={() => {
          setLobbyKey(k => k + 1)
          setPhase('select')
        }}
      />
    )
  }

  const currentHand = gameMode === 'ai'
    ? (currentTurn === 1 ? player1Hand : []) // Player 1's hand in AI mode
    : (currentTurn === playerNumber ? (playerNumber === 1 ? player1Hand : player2Hand) : [])
  const isMyTurn = (gameMode === 'ai' && currentTurn === 1) || (gameMode !== 'ai' && (currentTurn === playerNumber || gameMode === 'local'))

  const player1Name = gameMode === 'ai' ? playerName : gameMode === 'network' ? session?.player1_name || playerName : 'Player 1'
  const player2Name = gameMode === 'ai' ? 'AI' : gameMode === 'network' ? session?.player2_name || 'Player 2' : 'Player 2'

  // Combo label for selected cards
  const selectedCardObjects = selectedCards.map(id => currentHand.find(c => c.id === id)!).filter(Boolean)
  const comboCheck = selectedCardObjects.length > 0 ? isValidPlay(selectedCardObjects) : null
  const canBeat = comboCheck?.valid ? beatsLastPlay(selectedCardObjects) : false
  const comboStatus = !comboCheck ? null
    : !comboCheck.valid ? 'invalid'
    : !canBeat ? 'weak'
    : 'valid'
  const comboText = !comboCheck ? null
    : !comboCheck.valid ? 'Invalid combo'
    : comboCheck.type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + (canBeat ? ' ✓' : ' — can\'t beat last play')

  return (
    <div className="bigtwo-container universal-game-room">
      <GameRoomHeader
        gameTitle="大老二 (Big Two)"
        gameIcon="🎴"
        mode={gameMode as 'local' | 'ai' | 'network'}
        players={
          gameMode === 'network'
            ? [
                { name: player1Name, avatar: '🎴', status: 'active' },
                { name: player2Name, avatar: '🃏', status: session?.player2_name ? 'active' : 'disconnected' },
              ]
            : gameMode === 'ai'
            ? [
                { name: playerName, avatar: '🎴', status: currentTurn === 1 ? 'active' : 'thinking' },
                { name: `AI (${aiDifficulty})`, avatar: '🤖', status: currentTurn === 2 ? 'thinking' : 'active' },
              ]
            : [
                { name: 'Player 1', avatar: '🎴', status: currentTurn === 1 ? 'active' : 'thinking' },
                { name: 'Player 2', avatar: '🃏', status: currentTurn === 2 ? 'active' : 'thinking' },
              ]
        }
        sessionCode={gameMode === 'network' ? session?.session_code : undefined}
        onBack={() => navigate('/')}
      />

      {/* Voice Chat Component (network mode only) */}
      {gameMode === 'network' && (
        <VoiceChat
          connectionStatus={voice.connectionStatus}
          voiceStatus={{ localMuted: voice.isMuted, remoteMuted: false, localConnected: voice.isConnected, remoteConnected: voice.isConnected }}
          playerName={playerName}
          onConnect={voice.connect}
          onToggleMute={voice.toggleMute}
        />
      )}

      {phase === 'finished' && (
        <div className="winner-overlay">
          <h2>🎉 {
            gameMode === 'ai'
              ? (winner === 1 ? 'You Win!' : 'AI Wins!')
              : winner === playerNumber ? 'You Win!' : gameMode === 'local' ? `Player ${winner} Wins!` : 'Opponent Wins!'
          }</h2>
          <button onClick={() => {
            dealCards();
            setPhase('playing');
            setWinner(null);
            // Track win if player won
            if (gameMode === 'ai' && winner === 1) {
              UserProfileManager.incrementWins()
            }
          }}>
            Play Again
          </button>
        </div>
      )}

      <div className="game-room-content">
        <div className="game-board game-board-area">
          {/* Opponent area */}
          <div className={`opponent-area ${currentTurn === 2 ? 'active-turn' : ''}`}>
            <div className="opponent-info">
              <h3>
                {gameMode === 'ai'
                  ? `🤖 AI (${aiDifficulty.charAt(0).toUpperCase() + aiDifficulty.slice(1)})`
                  : gameMode === 'network'
                  ? `👤 ${player2Name}`
                  : '👤 Player 2'}
              </h3>
              {gameMode === 'ai' && currentTurn === 2 && (
                <div className="ai-thinking">🤔 Thinking...</div>
              )}
            </div>
            <div className="opponent-cards-wrap">
              <div className="opponent-cards">
                {Array.from({ length: Math.min(player2Hand.length, 13) }).map((_, i) => (
                  <div key={i} className="card-back" style={{ '--card-index': i, zIndex: i } as React.CSSProperties} />
                ))}
              </div>
              <span className="card-count-badge">{player2Hand.length}</span>
            </div>
          </div>

          {/* Last play area */}
          <div className="play-area">
            <div className="play-area-label">
              {lastPlay.length > 0 ? 'Last Play' : 'Table Empty'}
              {passCount > 0 && <span className="pass-badge">{passCount} pass{passCount > 1 ? 'es' : ''}</span>}
            </div>
            <div className="last-play">
              {lastPlay.length > 0 ? (
                lastPlay.map(card => (
                  <div
                    key={card.id}
                    className={`card ${card.suit === '♥' || card.suit === '♦' ? 'red' : ''}`}
                    aria-label={`${card.rank} of ${card.suit}`}
                  >
                    <span className="rank">{card.rank}</span>
                    <span className="suit">{card.suit}</span>
                  </div>
                ))
              ) : (
                <p className="empty-table-hint">Play any valid combo to start</p>
              )}
            </div>
          </div>

          {/* Player hand */}
          <div className={`player-area ${isMyTurn ? 'your-turn-area' : ''}`}>
            <div className="player-area-header">
              <h3 className={isMyTurn ? 'your-turn' : ''}>
                {isMyTurn
                  ? (gameMode === 'local' ? `🎴 Player ${currentTurn} — Your Turn` : '🎴 Your Turn')
                  : (gameMode === 'ai' ? '🤖 AI Turn...' : gameMode === 'network' ? '⏳ Opponent\'s Turn' : `⏳ Player ${currentTurn === 1 ? 2 : 1} — Waiting...`)}
              </h3>
              <button className="sort-btn" onClick={sortCurrentHand} title="Sort hand by rank">
                Sort ↕
              </button>
            </div>

            {comboText && (
              <div className={`combo-label combo-${comboStatus}`}>{comboText}</div>
            )}

            <div className="hand">
              {currentHand.map((card, index) => {
                const totalCards = currentHand.length
                const arcSpread = Math.min(120, totalCards * 10)
                const startAngle = -arcSpread / 2
                const angleStep = totalCards > 1 ? arcSpread / (totalCards - 1) : 0
                const rotation = startAngle + (angleStep * index)
                const selectIndex = selectedCards.indexOf(card.id) + 1

                return (
                  <div
                    key={card.id}
                    className={`card ${selectedCards.includes(card.id) ? 'selected' : ''} ${
                      card.suit === '♥' || card.suit === '♦' ? 'red' : ''
                    } ${!isMyTurn ? 'disabled' : ''}`}
                    style={{
                      '--card-rotation': `${rotation}deg`,
                      transform: `translateX(var(--card-half-neg)) rotateZ(${rotation}deg)`,
                      zIndex: selectedCards.includes(card.id) ? 101 : 50 + index,
                    } as React.CSSProperties}
                    data-select-index={selectIndex > 0 ? selectIndex : undefined}
                    onClick={() => isMyTurn && toggleCard(card.id)}
                    aria-label={`${card.rank} of ${card.suit}`}
                    role="button"
                    tabIndex={isMyTurn ? 0 : -1}
                  >
                    <span className="rank">{card.rank}</span>
                    <span className="suit">{card.suit}</span>
                  </div>
                )
              })}
            </div>

            {isMyTurn && (
              <>
                {playError && <div className="play-error">{playError}</div>}
                <div className="actions">
                  <button
                    onClick={playCards}
                    disabled={selectedCards.length === 0 || comboStatus === 'invalid'}
                    className={comboStatus === 'valid' ? 'play-ready' : ''}
                    aria-label={`Play ${selectedCards.length} selected card${selectedCards.length !== 1 ? 's' : ''}`}
                  >
                    {selectedCards.length > 0 ? `Play (${selectedCards.length})` : 'Play Cards'}
                  </button>
                  <button
                    onClick={pass}
                    disabled={lastPlay.length === 0}
                    aria-label="Pass your turn"
                  >
                    Pass
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <GameRoomSidebar
          mode={gameMode as 'local' | 'ai' | 'network'}
          gameState={{
            turn: currentTurn === 1 ? `${player1Name}'s turn` : `${player2Name}'s turn`,
            phase: winner ? 'Game Over' : 'Playing',
            score: {
              [player1Name]: `${player1Hand.length} cards`,
              [player2Name]: `${player2Hand.length} cards`,
            }
          }}
          messages={chatMessages}
          historyItems={historyItems}
          onSendMessage={handleSidebarMessage}
          collapsed={sidebar.collapsed}
          onToggleCollapse={sidebar.toggle}
          position={sidebar.position}
          quickReactions={true}
        />
      </div>
    </div>
  )
}
